import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { useUserSettings } from './UserSettingsContext';

export const ThemeContext = createContext();

const lightTheme = createTheme({
  palette: {
    mode: 'light',
  },
  typography: {
    fontFamily: 'ui-sans-serif,-apple-system,system-ui,Segoe UI,Helvetica,Apple Color Emoji,Arial,sans-serif,Segoe UI Emoji,Segoe UI Symbol',
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
  typography: {
    fontFamily: 'ui-sans-serif,-apple-system,system-ui,Segoe UI,Helvetica,Apple Color Emoji,Arial,sans-serif,Segoe UI Emoji,Segoe UI Symbol',
  },
});

export const ThemeContextProvider = ({ children }) => {
  const { settings, updateSettings, loading: settingsLoading } = useUserSettings();
  const [mode, setMode] = useState(settings.themeMode);

  useEffect(() => {
    if (!settingsLoading && settings.themeMode) {
      setMode(settings.themeMode);
    }
  }, [settings.themeMode, settingsLoading]);

  const theme = useMemo(() => {
    return mode === 'light' ? lightTheme : darkTheme;
  }, [mode]);

  const colorMode = useMemo(() => ({
    mode,
    toggleColorMode: async () => {
      const newMode = mode === 'light' ? 'dark' : 'light';
      setMode(newMode);
      await updateSettings({ themeMode: newMode });
    }
  }), [mode, updateSettings]);

  if (settingsLoading) {
    return null;
  }

  return (
    <ThemeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};